import {
  ApiRef,
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';

export interface TransitionStep {
  days: number;
  storageClass: string;
}

export interface LifecycleRule {
  id: string;
  status: 'Enabled' | 'Disabled';
  transitions: TransitionStep[];
  prefix?: string;
  tagKey?: string;
  tagValue?: string;
}

export interface StorageLifecycleApi {
  listBuckets(stackRef: string): Promise<string[]>;
  listStorageClasses(): Promise<string[]>;
  getLifecycleRules(stackRef: string, bucket: string): Promise<LifecycleRule[]>;
  putLifecycleRules(
    stackRef: string,
    bucket: string,
    rules: LifecycleRule[],
  ): Promise<LifecycleRule[]>;
}

export const storageLifecycleApiRef: ApiRef<StorageLifecycleApi> =
  createApiRef<StorageLifecycleApi>({
    id: 'plugin.storage-lifecycle.service',
  });

export class StorageLifecycleClient implements StorageLifecycleApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('storage-lifecycle');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${base}${path}`, init);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as T;
  }

  async listBuckets(stackRef: string): Promise<string[]> {
    const params = new URLSearchParams({ stack: stackRef });
    const { buckets } = await this.request<{ buckets: string[] }>(
      `/buckets?${params.toString()}`,
    );
    return buckets;
  }

  async listStorageClasses(): Promise<string[]> {
    const { storageClasses } = await this.request<{
      storageClasses: string[];
    }>('/storage-classes');
    return storageClasses;
  }

  async getLifecycleRules(
    stackRef: string,
    bucket: string,
  ): Promise<LifecycleRule[]> {
    const params = new URLSearchParams({ stack: stackRef });
    const { rules } = await this.request<{ rules: LifecycleRule[] }>(
      `/buckets/${encodeURIComponent(bucket)}/lifecycle?${params.toString()}`,
    );
    return rules;
  }

  async putLifecycleRules(
    stackRef: string,
    bucket: string,
    rules: LifecycleRule[],
  ): Promise<LifecycleRule[]> {
    const params = new URLSearchParams({ stack: stackRef });
    const { rules: saved } = await this.request<{ rules: LifecycleRule[] }>(
      `/buckets/${encodeURIComponent(bucket)}/lifecycle?${params.toString()}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules }),
      },
    );
    return saved;
  }
}
